/**
 * /app/$tenantSlug/leave/holidays — Me → Leave → Holidays: the company's
 * holidays for a year, with optional (restricted) holidays marked.
 *
 * Navigation rebuild (wave 3, 28 Oct 2026). Read-only for everyone: no
 * add / edit / delete here — HR manages the calendar at Manage → Leave →
 * Holidays (/holidays). The one action is the viewer's own: "Take This Day
 * Off" on an optional holiday (the same OptionalHolidayPickButton as the
 * Leave hub's upcoming-holidays card; the yearly allowance is enforced
 * server-side).
 *
 * GET /api/v1/holidays?year= answers any signed-in member with the active
 * holidays and the caller's own optional-holiday picks.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarHeart } from "lucide-react";
import { useHolidays } from "@/hooks/use-holidays";
import { toApiError } from "@/lib/api";
import { fmtDate, todayISO } from "@/lib/leave-format";
import { holidayYearOptions, sortActiveHolidays, upcomingFirst } from "@/lib/leave-holidays";
import {
  OptionalHolidayAllowanceNote,
  OptionalHolidayPickButton,
} from "@/components/leave/optional-holiday-pick";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/leave/holidays")({
  component: LeaveHolidaysRoute,
});

function LeaveHolidaysRoute() {
  const today = todayISO();
  const currentYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(currentYear);
  const years = useMemo(() => holidayYearOptions(currentYear), [currentYear]);

  const q = useHolidays({ year });
  const holidays = useMemo(() => upcomingFirst(sortActiveHolidays(q.data?.items), today), [q.data, today]);
  const hasOptional = holidays.some((h) => h.is_optional);

  return (
    <TimeAndLeaveShell
      title="Holidays"
      description="Your company's holidays. Optional holidays are working days unless you take them."
    >
      <div className="space-y-4">
        <div className="grid gap-1.5">
          <Label htmlFor="leave-holidays-year">Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger id="leave-holidays-year" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {q.data && (
          <OptionalHolidayAllowanceNote
            allowance={q.data.optional}
            hasOptional={hasOptional}
          />
        )}

        {q.isLoading && (
          <div className="space-y-3">
            {["s1", "s2", "s3"].map((k) => (
              <Skeleton key={k} className="h-12 w-full" />
            ))}
          </div>
        )}

        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t Load Holidays</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}

        {q.data && holidays.length === 0 && (
          <EmptyState
            icon={CalendarHeart}
            title={`No Holidays in ${year}`}
            description="Your company hasn't added holidays for this year yet."
          />
        )}

        {holidays.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y" data-testid="leave-holidays-list">
                {holidays.map((h) => {
                  const date = h.holiday_date.slice(0, 10);
                  const past = date < today;
                  return (
                    <li
                      key={h.id}
                      className={
                        past
                          ? "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-muted/30 px-4 py-3"
                          : "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3"
                      }
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={
                              past ? "font-medium text-muted-foreground" : "font-medium"
                            }
                          >
                            {h.name}
                          </span>
                          {h.is_optional && (
                            <Badge variant="outline" className="font-normal">
                              Optional
                            </Badge>
                          )}
                          {h.is_optional && h.picked && (
                            <Badge variant="secondary" className="font-normal" data-testid="holiday-picked-badge">
                              You picked this
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {fmtDate(date)}
                          {h.description ? ` · ${h.description}` : ""}
                        </div>
                      </div>
                      {h.is_optional && (
                        <OptionalHolidayPickButton
                          holiday={h}
                          allowance={q.data?.optional}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </TimeAndLeaveShell>
  );
}
